<template>
    <div class="vx-flex vx-flex-col vx-h-full" :class="{
        'vx-justify-between': !loading && !!contact?.id,
        'vx-justify-center vx-items-center': loading,
    }">
        <template v-if="loading">
            <vx-logo-loading></vx-logo-loading>
        </template>
        <template v-else>
            <conversation-nav v-if="!contact?.id" class="vx-justify-between">
                <span class="vx-text-base vx-font-extrabold vx-text-slate-100">{{ pageTitle }}</span>
                <vx-button v-if="FEATURES.contact_groups" size="sm" color="secondary" @click="changeGroupModal = true">
                    Change <font-awesome-icon :icon="faGrid2Plus"></font-awesome-icon>
                </vx-button>
            </conversation-nav>
            <template v-if="contact?.id">
                <conversation-title
                    v-show="!contactDetailsOpened"
                    @open:contactDetails="contactDetailsOpened = true"
                    @unloadContact="unmount">
                </conversation-title>
                <div v-show="!contactDetailsOpened"
                    class="vx-flex vx-flex-col vx-h-full vx-min-h-0 vx-bg-white vx-shadow-inner vx-border-0 vx-border-b vx-border-solid vx-border-slate-300">
                    <conversation-messages></conversation-messages>
                    <conversation-scheduled-messages v-if="onCorporate()"></conversation-scheduled-messages>
                </div>
                <conversation-actions v-show="!contactDetailsOpened"></conversation-actions>
                <conversation-details v-show="contactDetailsOpened" @close="contactDetailsOpened = false"></conversation-details>
            </template>
            <template v-else>
                <div class="vx-flex vx-flex-col vx-items-center vx-gap-10 vx-py-6 vx-px-4">
                    <vx-box color="info" class="vx-w-full vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500 vx-text-center">
                        Select a number in your browser to get started.
                    </vx-box>
                    <voxie-logo />
                </div>
            </template>
        </template>


        <vx-modal v-if="FEATURES.contact_groups" v-model:visible="changeGroupModal">
            <navbar-groups-dropdown
                class="!vx-p-0"
                :disableManageLink="true"
                @switching="loadingContact = true"
            ></navbar-groups-dropdown>
        </vx-modal>
    </div>
</template>
<script setup>
import VoxieLogo from '~/components/messages/browser-extension/components/misc/VoxieLogo.vue';
import ConversationTitle from '~/components/messages/browser-extension/components/ConversationTitle.vue';
import ConversationDetails from '~/components/messages/browser-extension/components/ConversationDetails.vue';
import ConversationActions from '~/components/messages/browser-extension/components/ConversationActions.vue';
import ConversationMessages from '~/components/messages/browser-extension/components/ConversationMessages.vue';
import ConversationScheduledMessages from '~/components/messages/browser-extension/components/ConversationScheduledMessages.vue';
import NavbarGroupsDropdown from '~/components/navbar/NavbarGroupsDropdown.vue'
import { useTitle, useUrlSearchParams } from '@vueuse/core';
import { onMounted, ref, inject, computed } from 'vue';
import { actionsClient, contactsClient, groupContactsClient } from '~/services';
import { e164Format, isValidPhoneNumber } from '~/utils/phoneNumber';
import { VxLogoLoading, VxButton, VxBox, VxModal } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ConversationNav from '~/components/messages/browser-extension/components/ConversationNav.vue'
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { catchRequestError } from '~/utils/request';

const $toasted = useToasted();
const FEATURES = inject('FEATURES');

const queryParams = useUrlSearchParams();
const currentTeam = Spark.state.currentTeam;
const teamId = Spark.state.currentTeam.id;

const { currentGroup, onCorporate }  = useCurrentGroup();
const { loading: loadingContact, contact, mount, unmount } = useContactDetails();

const pageTitle = FEATURES.contact_groups && currentGroup.value?.name ?
    currentGroup.value.name :
    currentTeam.name || 'Voxie';

useTitle(
    `Voxie • ${pageTitle}`
);

const contactDetailsOpened = ref(false);
const changeGroupModal = ref(false);

const loading = computed(() => searchingContact.value || loadingContact.value)

const searchingContact = ref(false);
const searchContact = async (params) => {
    if (currentGroup.value?.group_id) {
        return groupContactsClient.groupContactsList(teamId, currentGroup.value.group_id, params);
    } else {
        return contactsClient.searchContacts(teamId, params)
    }
}

const createContact = async (phoneNumber) => {
    if (currentGroup.value?.group_id) {
        return groupContactsClient.groupContactSave(teamId, currentGroup.value.group_id, { phone: phoneNumber });
    } else {
        return contactsClient.contactSave(teamId, { phone: phoneNumber });
    }
}

const loadContact = async (phoneNumber) => {
    unmount();

    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
        searchingContact.value = false;

        return;
    }

    if (searchingContact.value) {
        return;
    }

    searchingContact.value = true;

    try {
        const params = {
            'filter[phone]': phoneNumber,
        };

        const response = await searchContact(params);

        if (response.data?.data && response.data?.data?.[0]?.id && response.data?.data?.[0].phone === phoneNumber) {
            contact.value.id = response.data.data[0].id;
        } else {
            contact.value = (await createContact(phoneNumber))?.data;
            actionsClient.send('contact_added');
        }

        mount(contact.value.id, currentGroup.value?.id);
    } catch (e) {
        $toasted.error(catchRequestError(e));
    } finally {
        searchingContact.value = false;
    }
}

document.addEventListener('VoxieExtension:PhoneNumberDetected', async (event) => {
    const phoneNumber = e164Format(event?.detail?.phoneNumber);

    if (!phoneNumber) {
        return;
    }

    if (queryParams.phoneNumber) {
        queryParams.phoneNumber = phoneNumber;
    } else {
        queryParams.action = 'VoxieExtension:PhoneNumberDetected';
        queryParams.data = phoneNumber;
    }

    loadContact(phoneNumber);
});

document.addEventListener('VoxieExtension:ChangeGroup', async () => {
    changeGroupModal.value = true;
});

Echo.private(`contacts.${teamId}`)
    .listen('ContactDetailsUpdated', (payload) => {
        contact.value = payload.contact;
    });

onMounted(async () => {
    if (!queryParams.phoneNumber && !queryParams.action) {
        return;
    }

    if (queryParams.action === 'VoxieExtension:ChangeGroup') {
        changeGroupModal.value = true;

        // Do not show change group on next load
        //  since query params will be stored always unless we clear them or change.
        queryParams.action = undefined;
    }

    if (
        (queryParams.phoneNumber && !e164Format(queryParams.phoneNumber)) ||
        (queryParams.action === 'VoxieExtension:PhoneNumberDetected' && !e164Format(queryParams.data))
    ) {
        return;
    } else {
        const phoneNumber = e164Format(queryParams.phoneNumber || queryParams.data);
        loadContact(phoneNumber);
    }
});

</script>
